import { z } from 'zod';
import { paginationQuery } from './common.js';
import { partyIdentificationType } from './customers.js';
import { CREDIT_SALE_CONDITIONS, EXONERATION_REQUIRES_ARTICLE, IVA_RATES, isServiceLine, ivaRate, PAYMENT_METHODS, SALE_CONDITIONS, } from './hacienda.js';
import { add, max, min, mul, percent, sub, toNumber, toUnits } from './money.js';
/* ------------------------------------------------------------------ */
/* Reglas fiscales                                                     */
/* ------------------------------------------------------------------ */
/** CABYS que Hacienda trata como no sujetos: se emiten con tarifa 11 al 0%. */
export const CABYS_NOT_SUBJECT = [
    '9231000000100',
    '9231000000200',
    '9292000000300',
    '7211100000100',
    '3261000000100',
];
/** IVA devuelto: servicios de salud (tarifa 4%) pagados con tarjeta. */
const IVA_RETURN_CODE = '04';
const CARD_PAYMENT = '02';
const ivaCodes = IVA_RATES.map((r) => r.code);
/** Códigos del catálogo de Hacienda: uno fuera de él produciría un XML que el Ministerio rechaza. */
const saleConditionCode = z
    .string()
    .refine((c) => SALE_CONDITIONS.some((s) => s.code === c), 'Condición de venta inválida');
const paymentMethodCode = z
    .string()
    .refine((c) => PAYMENT_METHODS.some((m) => m.code === c), 'Medio de pago inválido');
/* ------------------------------------------------------------------ */
/* Entrada                                                             */
/* ------------------------------------------------------------------ */
export const exonerationInput = z
    .object({
    documentType: z.string().regex(/^\d{2}$/),
    documentTypeOther: z.string().trim().min(5).max(100).nullable().optional(),
    number: z.string().trim().min(3, 'Requerido').max(40),
    article: z.string().trim().max(6).nullable().optional(),
    inciso: z.string().trim().max(6).nullable().optional(),
    institution: z.string().regex(/^\d{2}$/),
    institutionOther: z.string().trim().min(5).max(160).nullable().optional(),
    date: z.iso.datetime({ offset: true }).or(z.iso.date()),
    /** TarifaExonerada: puntos porcentuales exonerados (no puede superar la tarifa del IVA). */
    rate: z.number({ error: 'Indicá la tarifa' }).min(0).max(13),
})
    .superRefine((v, ctx) => {
    if (v.documentType === '99' && !v.documentTypeOther) {
        ctx.addIssue({ code: 'custom', path: ['documentTypeOther'], message: 'Describí el tipo de documento' });
    }
    if (EXONERATION_REQUIRES_ARTICLE.includes(v.documentType) && !v.article) {
        ctx.addIssue({ code: 'custom', path: ['article'], message: 'Este tipo de exoneración exige artículo' });
    }
    if (v.documentType === '03' && !v.inciso) {
        ctx.addIssue({ code: 'custom', path: ['inciso'], message: 'Indicá el inciso' });
    }
    if (v.institution === '99' && !v.institutionOther) {
        ctx.addIssue({ code: 'custom', path: ['institutionOther'], message: 'Indicá la institución' });
    }
});
/** Tipos de código comercial del XML v4.4 (CodigoComercial/Tipo). */
export const COMMERCIAL_CODE_TYPES = [
    { code: '01', name: 'Código del vendedor' },
    { code: '02', name: 'Código del comprador' },
    { code: '03', name: 'Asignado por la industria' },
    { code: '04', name: 'Uso interno' },
    { code: '99', name: 'Otros' },
];
/** Código comercial de una línea; el XML admite hasta 5 por línea. */
export const commercialCodeInput = z.object({
    type: z.enum(['01', '02', '03', '04', '99']).default('01'),
    code: z.string().trim().min(1).max(20),
});
export const MAX_COMMERCIAL_CODES = 5;
export const documentLineInput = z.object({
    productId: z.uuid().nullable().optional(),
    code: z.string().trim().max(20).nullable().optional(),
    /** Todos los códigos comerciales de la línea (API de integración); si faltan, se usa `code` como tipo 01. */
    codes: z.array(commercialCodeInput).max(MAX_COMMERCIAL_CODES).nullable().optional(),
    /** Nota de la línea (se imprime bajo la descripción). */
    notes: z.string().trim().max(500).nullable().optional(),
    cabysCode: z
        .string()
        .regex(/^\d{13}$/, 'CABYS de 13 dígitos')
        .nullable()
        .optional(),
    description: z.string().trim().min(1, 'Requerido').max(200),
    unit: z.string().min(1).max(20).default('Unid'),
    quantity: z.coerce
        .number()
        .positive('Mayor a cero')
        .max(9_999_999_999_999)
        // El XML imprime la cantidad con 3 decimales: más precisión descuadraría cantidad × precio.
        .refine((v) => Math.abs(Math.round(v * 1000) - v * 1000) < 1e-6, 'Máximo 3 decimales'),
    unitPrice: z.coerce.number().min(0, 'No puede ser negativo').max(1e13),
    discountPct: z.coerce.number().min(0).max(100).default(0),
    /** Monto exacto del descuento (API de integración); si viene, manda sobre el porcentaje. */
    discountAmount: z.coerce.number().min(0).nullable().optional(),
    discountReason: z.string().trim().max(80).nullable().optional(),
    ivaCode: z.enum(ivaCodes).default('08'),
    exoneration: exonerationInput.nullable().optional(),
    pharmaRegistry: z.string().trim().max(100).nullable().optional(),
    pharmaForm: z.string().trim().max(10).nullable().optional(),
});
export const otherChargeInput = z.object({
    type: z
        .string()
        .regex(/^\d{2}$/)
        .default('06'),
    description: z.string().trim().min(3).max(160),
    percentage: z.coerce.number().min(0).max(100).nullable().optional(),
    amount: z.coerce.number().min(0),
});
/** Receptor puntual sin ficha de cliente (típico en tiquetes). */
export const receiverInput = z.object({
    name: z.string().trim().min(2).max(160),
    identificationType: partyIdentificationType.nullable().optional(),
    identification: z.string().trim().max(20).nullable().optional(),
    email: z.email().nullable().optional(),
});
/** Tipos de documento de referencia que son comprobantes electrónicos (la clave tiene 50 dígitos). */
const ELECTRONIC_REFERENCE_TYPES = ['01', '02', '03', '04', '08', '09', '10'];
export const referenceInput = z
    .object({
    /** Comprobante propio al que se refiere (NC/ND); si no, datos de uno externo. */
    documentId: z.uuid().nullable().optional(),
    documentType: z
        .string()
        .regex(/^\d{2}$/)
        .optional(),
    /** Texto del tipo cuando es "99 otros" (5 a 100 caracteres). */
    documentTypeOther: z.string().trim().min(5, 'Mínimo 5 caracteres').max(100).nullable().optional(),
    /** Clave de 50 dígitos de un comprobante electrónico, o el número libre de un contrato, SICOP, etc. */
    key: z.string().trim().min(1).max(50).nullable().optional(),
    issuedAt: z.iso.datetime({ offset: true }).nullable().optional(),
    code: z
        .string()
        .regex(/^\d{2}$/)
        .default('01'),
    /** Texto de la razón cuando es "99 otros" (5 a 100 caracteres). */
    codeOther: z.string().trim().min(5, 'Mínimo 5 caracteres').max(100).nullable().optional(),
    reason: z.string().trim().min(3, 'Indicá la razón').max(180),
})
    .superRefine((v, ctx) => {
    const type = v.documentType ?? '01';
    if (v.key && ELECTRONIC_REFERENCE_TYPES.includes(type) && !/^\d{50}$/.test(v.key)) {
        ctx.addIssue({ code: 'custom', path: ['key'], message: 'Clave de 50 dígitos' });
    }
    if (type === '99' && !v.documentTypeOther) {
        ctx.addIssue({ code: 'custom', path: ['documentTypeOther'], message: 'Describí el tipo de documento' });
    }
    if (v.code === '99' && !v.codeOther) {
        ctx.addIssue({ code: 'custom', path: ['codeOther'], message: 'Describí la razón' });
    }
});
export const documentKindSchema = z.enum(['FE', 'TE', 'NC', 'ND', 'FEC']);
export const documentInput = z
    .object({
    kind: documentKindSchema,
    branchId: z.uuid().nullable().optional(),
    terminalId: z.uuid().nullable().optional(),
    customerId: z.uuid().nullable().optional(),
    receiver: receiverInput.nullable().optional(),
    receiverActivity: z
        .string()
        .regex(/^\d{4,6}(\.\d)?$/)
        .nullable()
        .optional(),
    saleCondition: saleConditionCode.default('01'),
    saleConditionOther: z.string().trim().min(5).max(100).nullable().optional(),
    creditDays: z.coerce.number().int().min(0).max(99999).nullable().optional(),
    // Un solo medio de pago: el XML lleva únicamente el primero y de él depende el IVA devuelto.
    paymentMethods: z
        .array(paymentMethodCode)
        .min(1, 'Elegí un medio de pago')
        .max(1, 'Solo se admite un medio de pago'),
    currency: z.enum(['CRC', 'USD', 'EUR']).default('CRC'),
    exchangeRate: z.coerce.number().positive().nullable().optional(),
    notes: z.string().trim().max(1000).nullable().optional(),
    terms: z.string().trim().max(4000).nullable().optional(),
    reference: referenceInput.nullable().optional(),
    otherCharges: z.array(otherChargeInput).max(15).default([]),
    lines: z.array(documentLineInput).min(1, 'Agregá al menos una línea').max(1000),
    emailTo: z.array(z.email()).max(10).default([]),
    draft: z.boolean().default(false),
})
    .superRefine((v, ctx) => {
    if ((v.kind === 'FE' || v.kind === 'FEC') && !v.customerId) {
        ctx.addIssue({
            code: 'custom',
            path: ['customerId'],
            message: v.kind === 'FEC' ? 'Elegí el proveedor' : 'La factura necesita un cliente',
        });
    }
    if ((v.kind === 'NC' || v.kind === 'ND') && !v.reference) {
        ctx.addIssue({ code: 'custom', path: ['reference'], message: 'Indicá el comprobante que se corrige' });
    }
    if (v.reference && !v.reference.documentId && !v.reference.key) {
        ctx.addIssue({ code: 'custom', path: ['reference', 'key'], message: 'Indicá la clave del comprobante' });
    }
    if (CREDIT_SALE_CONDITIONS.includes(v.saleCondition) && !v.creditDays) {
        ctx.addIssue({ code: 'custom', path: ['creditDays'], message: 'Indicá el plazo de crédito' });
    }
    if (v.saleCondition === '99' && !v.saleConditionOther) {
        ctx.addIssue({
            code: 'custom',
            path: ['saleConditionOther'],
            message: 'Describí la condición de venta',
        });
    }
    if (v.kind === 'FE' && v.lines.length > 1000) {
        ctx.addIssue({ code: 'custom', path: ['lines'], message: 'Máximo 1000 líneas' });
    }
    v.lines.forEach((line, index) => {
        // Un borrador puede guardarse con líneas incompletas; el CABYS se exige al emitir.
        if (!line.cabysCode && !v.draft) {
            ctx.addIssue({ code: 'custom', path: ['lines', index, 'cabysCode'], message: 'Falta el CABYS' });
        }
        if (line.exoneration && line.exoneration.rate > ivaRate(line.ivaCode)) {
            ctx.addIssue({
                code: 'custom',
                path: ['lines', index, 'exoneration', 'rate'],
                message: 'La tarifa exonerada no puede superar la del IVA',
            });
        }
    });
});
/**
 * Cálculo único de un comprobante v4.4. Lo usan la vista previa del formulario y
 * el API al emitir; así lo que el usuario ve es exactamente lo que va al XML.
 */
export function computeDocument(input) {
    const card = input.paymentMethods?.[0] === CARD_PAYMENT;
    const buckets = {
        services: { taxable: 0n, exempt: 0n, exonerated: 0n, notSubject: 0n },
        goods: { taxable: 0n, exempt: 0n, exonerated: 0n, notSubject: 0n },
    };
    const breakdown = new Map();
    // Códigos con impuesto aunque quede exonerado del todo: el desglose los conserva con monto 0.
    const taxedCodes = new Set();
    let totalSale = 0n;
    let totalDiscount = 0n;
    let totalTax = 0n;
    let ivaReturned = 0n;
    const lines = input.lines.map((line, index) => {
        const notSubject = !!line.cabysCode && CABYS_NOT_SUBJECT.includes(line.cabysCode);
        const ivaCode = notSubject ? '11' : line.ivaCode;
        const rate = toUnits(notSubject ? 0 : ivaRate(ivaCode));
        const isService = isServiceLine(line.cabysCode, line.unit);
        const gross = mul(toUnits(line.quantity), toUnits(line.unitPrice));
        const discount = line.discountAmount != null && line.discountAmount > 0
            ? min(gross, toUnits(line.discountAmount))
            : percent(gross, toUnits(line.discountPct ?? 0));
        const subtotal = sub(gross, discount);
        const tax = percent(subtotal, rate);
        const exonerated = line.exoneration && tax > 0n ? min(tax, percent(subtotal, toUnits(line.exoneration.rate))) : 0n;
        const taxNet = sub(tax, exonerated);
        const total = add(subtotal, taxNet);
        totalSale += gross;
        totalDiscount += discount;
        totalTax += taxNet;
        breakdown.set(ivaCode, (breakdown.get(ivaCode) ?? 0n) + taxNet);
        if (card && ivaCode === IVA_RETURN_CODE && isService)
            ivaReturned += taxNet;
        // Clasificación del Resumen. Hacienda exige TotalVenta = gravado + exento + exonerado + no sujeto, y
        // TotalVenta suma el MontoTotal de cada línea (antes del descuento): por eso los grupos se acumulan sobre
        // el bruto y no sobre el subtotal. El exonerado prorratea gravado/exonerado según el impuesto neto.
        const bucket = isService ? buckets.services : buckets.goods;
        if (exonerated > 0n) {
            const taxable = tax > 0n ? max(0n, (gross * taxNet) / tax) : 0n;
            bucket.taxable += taxable;
            bucket.exonerated += gross - taxable;
        }
        else if (ivaCode === '01' || ivaCode === '11') {
            bucket.notSubject += gross;
        }
        else if (ivaCode === '10' || rate === 0n) {
            bucket.exempt += gross;
        }
        else {
            bucket.taxable += gross;
        }
        if (tax > 0n)
            taxedCodes.add(ivaCode);
        return {
            lineNumber: index + 1,
            isService,
            ivaCode,
            taxRate: toNumber(rate),
            grossAmount: toNumber(gross),
            discountAmount: toNumber(discount),
            subtotal: toNumber(subtotal),
            taxAmount: toNumber(tax),
            exonerationAmount: toNumber(exonerated),
            taxNet: toNumber(taxNet),
            total: toNumber(total),
        };
    });
    const otherCharges = add(...(input.otherCharges ?? []).map((c) => toUnits(c.amount)));
    const netSale = sub(totalSale, totalDiscount);
    const s = buckets.services;
    const g = buckets.goods;
    return {
        lines,
        summary: {
            servicesTaxable: toNumber(s.taxable),
            servicesExempt: toNumber(s.exempt),
            servicesExonerated: toNumber(s.exonerated),
            servicesNotSubject: toNumber(s.notSubject),
            goodsTaxable: toNumber(g.taxable),
            goodsExempt: toNumber(g.exempt),
            goodsExonerated: toNumber(g.exonerated),
            goodsNotSubject: toNumber(g.notSubject),
            totalTaxable: toNumber(s.taxable + g.taxable),
            totalExempt: toNumber(s.exempt + g.exempt),
            totalExonerated: toNumber(s.exonerated + g.exonerated),
            totalNotSubject: toNumber(s.notSubject + g.notSubject),
            totalSale: toNumber(totalSale),
            totalDiscount: toNumber(totalDiscount),
            totalNetSale: toNumber(netSale),
            taxBreakdown: [...breakdown.entries()]
                .filter(([code, amount]) => amount !== 0n || taxedCodes.has(code))
                .map(([ivaCode, amount]) => ({ ivaCode, amount: toNumber(amount) })),
            totalTax: toNumber(totalTax),
            totalIvaReturned: toNumber(ivaReturned),
            totalOtherCharges: toNumber(otherCharges),
            total: toNumber(add(netSale, totalTax, otherCharges) - ivaReturned),
        },
    };
}
/* ------------------------------------------------------------------ */
/* Salida                                                              */
/* ------------------------------------------------------------------ */
export const documentListQuery = paginationQuery.extend({
    kind: z.union([documentKindSchema, z.enum(['NOTES'])]).optional(),
    status: z
        .enum(['draft', 'pending', 'sent', 'processing', 'accepted', 'rejected', 'error', 'failed', 'internal'])
        .optional(),
    payment: z.enum(['paid', 'unpaid', 'partial', 'overdue']).optional(),
    customerId: z.uuid().optional(),
    branchId: z.uuid().optional(),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
});
//# sourceMappingURL=documents.js.map